import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Section } from 'foundations-components/transitional/components/Section';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { View } from 'foundations-components/components/View';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_DESCRIPTION = 'epIssueButton.badgeDescription';
const IssueWrapper = styled.span.withConfig({
  displayName: "EPIssueButton__IssueWrapper"
})(["text-align:left;a{text-decoration:", ";}"], ({
  isTrellis
}) => isTrellis ? 'underline' : 'none');
const Issues = ({
  issues,
  iconBgColor,
  issueLabel,
  onIssueClick,
  isListStyled = true
}) => {
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  return /*#__PURE__*/_jsxs(Section, {
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(MediaObject, {
        itemLeft: /*#__PURE__*/_jsx(IconCircle, {
          icon: WarningIcon,
          size: "100",
          padding: "100",
          color: "icon-core-on-fill-default",
          backgroundColor: iconBgColor
        }),
        marginInlineStart: "800",
        children: /*#__PURE__*/_jsx("strong", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: issueLabel
          })
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(List, {
        ordered: true,
        styled: isListStyled,
        children: issues.map((issue, index) => {
          const {
            node
          } = issue;
          return /*#__PURE__*/_jsx(ListItem, {
            marginInlineStart: "200",
            marginInlineEnd: "200",
            children: /*#__PURE__*/_jsx(Button, {
              use: "unstyled",
              onClick: () => onIssueClick(issue),
              children: /*#__PURE__*/_jsx(IssueWrapper, {
                isTrellis: isTrellis,
                children: node
              })
            })
          }, index);
        })
      })
    })]
  });
};
const IssueButtonWrapper = styled.div.withConfig({
  displayName: "EPIssueButton__IssueButtonWrapper"
})(["white-space:nowrap;"]);
function transformPopoverWidthToString(popoverWidth) {
  if (typeof popoverWidth === 'number') {
    return `${popoverWidth}px`;
  }
  return;
}

// Borrowed from foundations-components/transitional/components/Button/Button.helpers.ts
function getButtonTokens(buttonSize) {
  switch (buttonSize) {
    case 'md':
    case 'medium':
      return {
        iconButtonSize: 'small',
        paddingBlock: '300',
        maxBlockSize: '40px'
      };
    case 'xs':
    case 'extra-small':
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '100',
        maxBlockSize: '24px'
      };
    case 'sm':
    case 'small':
    default:
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '200',
        maxBlockSize: '32px'
      };
  }
}
const DropdownIconButton = buttonProps => /*#__PURE__*/_jsx(IconButton, Object.assign({}, buttonProps, {
  children: /*#__PURE__*/_jsx(DownIcon, {})
}));
const RenderEpIssueAction = ({
  action
}) => {
  const {
    label,
    onClick,
    id,
    ActionTooltip,
    disabled: actionDisabled
  } = action;
  const button = /*#__PURE__*/_jsx(Button, {
    "data-test-id": id,
    onClick: onClick,
    disabled: actionDisabled !== null && actionDisabled !== void 0 ? actionDisabled : false,
    children: label
  });
  if (ActionTooltip) {
    return /*#__PURE__*/_jsx(ActionTooltip, {
      children: button
    }, `${id}-tooltip`);
  }
  return button;
};
const EPIssueButton = ({
  actions,
  buttonSize = 'small',
  children,
  'data-test': dataTest,
  disabled = false,
  errorLabel = 'epIssueButton.errorLabel',
  errors,
  infoLabel = 'epIssueButton.infoLabel',
  infos = [],
  loading = false,
  loadingFailed = false,
  onButtonClick,
  onDropdownOpenComplete,
  onIssueClick,
  onMouseEnter,
  popoverPlacement = 'bottom left',
  popoverWidth = 300,
  popoverInlineSize,
  tooltipOverride,
  warningLabel = 'epIssueButton.warningLabel',
  warnings,
  isListStyled = true,
  useDropdownButton = false
}) => {
  const [canPopupOpen, setCanPopupOpen] = useState(false);
  function handleButtonClick() {
    setCanPopupOpen(false);
    onButtonClick();
  }
  function hasIssues() {
    return !!errors.length || !!warnings.length || !!infos.length;
  }
  function handleMouseEnterButton() {
    if (hasIssues()) {
      setCanPopupOpen(true);
    }
    if (onMouseEnter) {
      onMouseEnter();
    }
  }
  function renderPopOverContent() {
    return /*#__PURE__*/_jsxs(View, {
      overflowY: "auto",
      maxBlockSize: "50vh",
      marginBlockStart: "800",
      children: [!!errors.length && /*#__PURE__*/_jsx(Issues, {
        issues: errors,
        iconBgColor: "fill-alert-default",
        issueLabel: errorLabel,
        onIssueClick: onIssueClick,
        isListStyled: isListStyled
      }), !!warnings.length && /*#__PURE__*/_jsx(Issues, {
        issues: warnings,
        iconBgColor: "fill-caution-default",
        issueLabel: warningLabel,
        onIssueClick: onIssueClick
      }), !!infos.length && /*#__PURE__*/_jsx(Issues, {
        issues: infos,
        iconBgColor: "fill-info-default",
        issueLabel: infoLabel,
        onIssueClick: onIssueClick,
        isListStyled: isListStyled
      })]
    });
  }
  function renderDropdownButton(props) {
    return /*#__PURE__*/_jsxs(LoadingButton, Object.assign({}, props, {
      use: "primary",
      size: buttonSize,
      disabled: !!errors.length || disabled || loading,
      onMouseEnter: handleMouseEnterButton,
      loading: loading,
      failed: loadingFailed,
      paddingInline: "400",
      children: [children, /*#__PURE__*/_jsx(DownIcon, {})]
    }));
  }
  function renderIssueButton() {
    const {
      iconButtonSize,
      maxBlockSize,
      paddingBlock
    } = getButtonTokens(buttonSize);

    // If useDropdownButton is true and actions exist, render single Dropdown with "Publish" text
    if (useDropdownButton && actions && actions.length > 0) {
      return /*#__PURE__*/_jsx(IssueButtonWrapper, {
        children: /*#__PURE__*/_jsx(NotificationBadge, {
          badgeDescription: I18n.text(BADGE_DESCRIPTION),
          badgeLabel: errors.length,
          showBadge: !!errors.length && !tooltipOverride,
          "data-test": dataTest,
          "data-test-num-errors": errors.length,
          children: /*#__PURE__*/_jsx(Dropdown, {
            "data-test-id": "issue-dropdown",
            Button: renderDropdownButton,
            onOpenComplete: onDropdownOpenComplete,
            children: /*#__PURE__*/_jsx(List, {
              wrapChildren: true,
              children: actions.map(action => /*#__PURE__*/_jsx(RenderEpIssueAction, {
                action: action
              }, action.id))
            })
          })
        })
      });
    }

    // Original ButtonGroup rendering - Publish and Icon as separate buttons
    return /*#__PURE__*/_jsx(IssueButtonWrapper, {
      children: /*#__PURE__*/_jsx(NotificationBadge, {
        badgeDescription: I18n.text(BADGE_DESCRIPTION),
        badgeLabel: errors.length,
        showBadge: !!errors.length && !tooltipOverride,
        "data-test": dataTest,
        "data-test-num-errors": errors.length,
        children: /*#__PURE__*/_jsxs(ButtonGroup, {
          children: [/*#__PURE__*/_jsx(LoadingButton, {
            disabled: !!errors.length || disabled,
            use: "primary",
            size: buttonSize,
            onClick: handleButtonClick,
            onMouseEnter: handleMouseEnterButton,
            loading: loading,
            failed: loadingFailed,
            children: children
          }), actions && actions.length > 0 && /*#__PURE__*/_jsx(Dropdown, {
            "data-test-id": "issue-dropdown",
            Button: DropdownIconButton,
            buttonSize: iconButtonSize,
            buttonStyles: {
              borderInlineStartColor: 'border-core-on-fill-default',
              borderInlineStartWidth: '100',
              borderInlineStartStyle: 'solid',
              paddingBlock,
              paddingInline: '100',
              // HACK: Remove once https://git.hubteam.com/HubSpot/foundations-components/issues/5632 is addressed
              maxBlockSize
            },
            buttonUse: "primary",
            caretRenderer: null,
            disabled: !!errors.length || disabled || loading,
            onOpenComplete: onDropdownOpenComplete,
            children: /*#__PURE__*/_jsx(List, {
              wrapChildren: true,
              children: actions.map(action => /*#__PURE__*/_jsx(RenderEpIssueAction, {
                action: action
              }, action.id))
            })
          })]
        })
      })
    });
  }
  if (tooltipOverride) {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: popoverPlacement,
      title: tooltipOverride,
      children: renderIssueButton()
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: canPopupOpen && hasIssues(),
    closeOnOutsideClick: true,
    onOpenChange: () => setCanPopupOpen(false),
    onFocusLeave: () => setCanPopupOpen(false),
    placement: popoverPlacement,
    inlineSize: popoverInlineSize !== null && popoverInlineSize !== void 0 ? popoverInlineSize : transformPopoverWidthToString(popoverWidth),
    autoPlacement: false,
    Content: renderPopOverContent,
    "data-test-id": "issue-popover",
    children: renderIssueButton()
  });
};
EPIssueButton.propTypes = {
  actions: PropTypes.array,
  buttonSize: PropTypes.string,
  children: PropTypes.node.isRequired,
  'data-test': PropTypes.string,
  disabled: PropTypes.bool,
  errorLabel: PropTypes.string,
  errors: PropTypes.array.isRequired,
  infoLabel: PropTypes.string,
  infos: PropTypes.array,
  onButtonClick: PropTypes.func.isRequired,
  onDropdownOpenComplete: PropTypes.func,
  onIssueClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  popoverPlacement: PropTypes.string,
  popoverWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipOverride: PropTypes.node,
  warningLabel: PropTypes.string,
  warnings: PropTypes.array.isRequired
};
export default EPIssueButton;